import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

const fieldClass =
  "h-9 w-full min-w-0 rounded-[0.8rem] border-line bg-background text-sm shadow-none";

const NUMERIC_KEYS = new Set(["taxValue", "netAmount", "carriage", "totalAmount"]);

function Field({
  id,
  label,
  value,
  onChange,
  disabled,
  error,
  changed = false,
  type = "text",
  inputMode,
  className,
}) {
  return (
    <div className={cn("invoice-detail-field", className)}>
      {/* The tag is a sibling of the label, not inside it, so the input's
          accessible name stays "Company name" rather than "Company nameEdited". */}
      <div className="flex items-center">
        <label htmlFor={id} className="invoice-detail-label">
          {label}
        </label>
        {changed && <span className="invoice-detail-edited-tag">Edited</span>}
      </div>
      <Input
        id={id}
        type={type}
        inputMode={inputMode}
        value={value ?? ""}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        aria-invalid={Boolean(error)}
        data-changed={changed ? "true" : undefined}
        className={cn(fieldClass, error && "border-destructive")}
      />
      {error && (
        <p className="invoice-detail-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export function InvoiceDetailForm({
  form: formValues,
  original: originalValues,
  fieldErrors: errors,
  viewOnly,
  onChange,
}) {
  // Any of these can be missing while a record is loading or after a partial
  // response, and every field below reads from them.
  const form = formValues ?? {};
  const original = originalValues ?? {};
  const fieldErrors = errors ?? {};
  const disabled = viewOnly;
  const set = (key) => (value) => onChange?.(key, value);
  const isChanged = (key) => {
    const now = String(form[key] ?? "").trim();
    const saved = String(original[key] ?? "").trim();
    if (now === saved) return false;
    // "100.50" and 100.5 are the same amount; only flag a real difference.
    if (NUMERIC_KEYS.has(key) && now !== "" && saved !== "") {
      const a = Number(now);
      const b = Number(saved);
      if (Number.isFinite(a) && Number.isFinite(b)) return a !== b;
    }
    return true;
  };

  return (
    <div className="invoice-detail-form invoice-detail-field-grid">
      {/* Both fields span: a supplier name is long free text, and half a
          column truncated most of them while the row's other half sat
          empty — the grid has no second field to pair it with here. */}
      <Field
        id="company-name"
        label="Company name"
        value={form.companyName}
        onChange={set("companyName")}
        changed={isChanged("companyName")}
        disabled={disabled}
        error={fieldErrors.companyName}
        className="invoice-detail-field-wide"
      />
      <Field
        id="company-address"
        label="Company address"
        value={form.companyAddress}
        onChange={set("companyAddress")}
        changed={isChanged("companyAddress")}
        disabled={disabled}
        error={fieldErrors.companyAddress}
        className="invoice-detail-field-wide"
      />
      <hr className="invoice-detail-divider" />
      <Field
        id="invoice-number"
        label="Invoice number"
        value={form.invoiceNumber}
        onChange={set("invoiceNumber")}
        changed={isChanged("invoiceNumber")}
        disabled={disabled}
        error={fieldErrors.invoiceNumber}
      />
      <Field
        id="invoice-date"
        label="Invoice date"
        type="date"
        value={form.invoiceDate}
        onChange={set("invoiceDate")}
        changed={isChanged("invoiceDate")}
        disabled={disabled}
        error={fieldErrors.invoiceDate}
      />
      <hr className="invoice-detail-divider" />
      <Field
        id="order-number"
        label="PO number"
        value={form.orderNumber}
        onChange={set("orderNumber")}
        changed={isChanged("orderNumber")}
        disabled={disabled}
        error={fieldErrors.orderNumber}
      />
      <Field
        id="vat-number"
        label="VAT number"
        value={form.vatNumber}
        onChange={set("vatNumber")}
        changed={isChanged("vatNumber")}
        disabled={disabled}
        error={fieldErrors.vatNumber}
      />
      <Field
        id="tax-value"
        label="Tax value"
        value={form.taxValue}
        onChange={set("taxValue")}
        changed={isChanged("taxValue")}
        disabled={disabled}
        error={fieldErrors.taxValue}
        inputMode="decimal"
      />
      <Field
        id="net-value"
        label="Net value"
        value={form.netAmount}
        onChange={set("netAmount")}
        changed={isChanged("netAmount")}
        disabled={disabled}
        error={fieldErrors.netAmount}
        inputMode="decimal"
      />
      <Field
        id="carriage"
        label="Carriage"
        value={form.carriage}
        onChange={set("carriage")}
        changed={isChanged("carriage")}
        disabled={disabled}
        error={fieldErrors.carriage}
        inputMode="decimal"
      />
      <Field
        id="total-value"
        label="Total"
        value={form.totalAmount}
        onChange={set("totalAmount")}
        changed={isChanged("totalAmount")}
        disabled={disabled}
        error={fieldErrors.totalAmount}
        inputMode="decimal"
      />
    </div>
  );
}
